import React from 'react';
import { InputNumber } from 'antd';
import { useTranslation } from 'react-i18next';
import type { CashBreakdown } from 'types/fms';
import LOCALS from 'commons/locals';
import type { CashDenomination } from '../cash-settlement-currency';

type CashBreakdownPanelProps = {
  cashBreakdown: CashBreakdown;
  bills: CashDenomination[];
  coins: CashDenomination[];
  disabled: boolean;
  currencySymbol: string;
  totalAmount: number;
  formatAmount: (amount: number | null | undefined) => string;
  onChange: (
    type: 'bills' | 'coins',
    denomination: string,
    count: number | null,
  ) => void;
  onClear: () => void;
};

const parseCount = (value: string | undefined) => {
  if (!value) return 0;
  const parsed = value.replace(/[^\d]/g, '');
  return parsed ? Number(parsed) : 0;
};

const getDisplayCount = (
  cashBreakdown: CashBreakdown,
  type: 'bills' | 'coins',
  denomination: string,
) => {
  const count = cashBreakdown[type]?.[denomination];

  return count === 0 ? 0 : count || null;
};

const CashBreakdownPanel: React.FC<CashBreakdownPanelProps> = ({
  cashBreakdown,
  bills,
  coins,
  disabled,
  currencySymbol,
  totalAmount,
  formatAmount,
  onChange,
  onClear,
}) => {
  const { t } = useTranslation();

  const renderDenominationRows = (
    type: 'bills' | 'coins',
    denominations: CashDenomination[],
    unitLabel: string,
  ) => {
    return denominations.map(({ denomination, label }) => (
      <div key={denomination} className="flex items-center justify-between py-2">
        <label className="text-sm text-gray-600 font-medium min-w-[100px]">
          {label}
        </label>
        <div className="flex items-center gap-2">
          <InputNumber
            value={getDisplayCount(cashBreakdown, type, denomination)}
            onChange={(value) => onChange(type, denomination, value)}
            className="w-24"
            min={0}
            precision={0}
            disabled={disabled}
            placeholder=""
            controls={false}
            parser={parseCount}
          />
          <span className="text-sm text-gray-500 font-medium min-w-[20px]">
            {t(unitLabel)}
          </span>
        </div>
      </div>
    ));
  };

  return (
    <div className="bg-gray-50 rounded-lg p-6 border-2 border-blue-100">
      <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">
          {t(LOCALS.daily_register_close_denomination_breakdown)}
        </h3>
        <button
          onClick={onClear}
          className="text-sm text-gray-500 hover:text-red-600 hover:bg-red-50 px-3 py-2 rounded-lg transition-all duration-200 font-medium"
          disabled={disabled}
        >
          {t(LOCALS.clear)}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-6">
          <h4 className="text-base font-semibold text-gray-700 pb-3 border-b border-gray-300">
            {t(LOCALS.bills)}
          </h4>
          <div className="space-y-4">
            {renderDenominationRows('bills', bills, LOCALS.bill_unit)}
          </div>
        </div>

        <div className="space-y-6">
          <h4 className="text-base font-semibold text-gray-700 pb-3 border-b border-gray-300">
            {t(LOCALS.coins)}
          </h4>
          <div className="space-y-4">
            {renderDenominationRows('coins', coins, LOCALS.coin_unit)}
          </div>
        </div>
      </div>

      <div className="mt-8 pt-6 border-t-2 border-gray-300">
        <div className="flex items-center justify-between py-4 bg-white px-6 rounded-lg border-2 border-blue-200 shadow-sm">
          <span className="text-lg font-semibold text-gray-900">
            {t(LOCALS.daily_register_close_denomination_total)}
          </span>
          <span className="text-2xl font-bold text-blue-600">
            {currencySymbol} {formatAmount(totalAmount)}
          </span>
        </div>
        <div className="mt-3 text-sm text-gray-600 text-center">
          {t(LOCALS.daily_register_close_breakdown_auto_reflect_note)}
        </div>
      </div>
    </div>
  );
};

export default CashBreakdownPanel;
